"use client";

import katex from "katex";
import { useMemo } from "react";
import { useOrdstod, type OrdstodClientData } from "./OrdstodContext";
import { markTextSegments } from "@/lib/ordstod/mark-client";
import { delaInlineKod, parseSegments, reparaProsa } from "@/lib/svar-format";
import { KATEX_OPTIONER } from "@/lib/katex-optioner";

interface InlineMathProps {
  source: string;
  // Uppgiftstext-ordstöd (fraga-fält) — se OrdstodContext. Default av så
  // svarsfält/alternativ/lösning/facit aldrig markeras av misstag; sätts bara
  // true på call sites som renderar uppgiftens/delfrågans fraga. Markering
  // sker ÄVEN i diagnos (läsförståelsestöd, medvetet).
  ordstod?: boolean;
}

// Precomputed lookup passed through the render helpers below — null when
// ordstod marking is off (prop false, no client data, or gating disabled
// server-side), so the whole text/bold/term pipeline degenerates back to the
// original plain-string rendering with zero extra work.
interface OrdstodRenderCtx {
  data: NonNullable<OrdstodClientData>;
  formIndex: Map<string, string>;
}

function wrapOrdstod(
  text: string,
  keyPrefix: string,
  ctx: OrdstodRenderCtx,
): React.ReactNode {
  const segments = markTextSegments(text, ctx.formIndex);
  if (segments.length === 1 && segments[0].kind === "text") return text;
  return segments.map((seg, i) => {
    if (seg.kind === "text") return <span key={`${keyPrefix}-m${i}`}>{seg.value}</span>;
    const lemma = seg.lemma as string;
    return (
      <span
        key={`${keyPrefix}-m${i}`}
        className="ordstod-ord"
        data-ordstod={lemma}
        data-ordstod-f={ctx.data.forklaring[lemma] ?? ""}
        data-ordstod-k={ctx.data.kontext[lemma] ?? ""}
        role="button"
        tabIndex={0}
      >
        {seg.value}
      </span>
    );
  });
}

function maybeMarkOrdstod(
  text: string,
  keyPrefix: string,
  ctx: OrdstodRenderCtx | null,
): React.ReactNode {
  return ctx ? wrapOrdstod(text, keyPrefix, ctx) : text;
}

type Align = "left" | "right" | "center";

type Block =
  | { kind: "text"; value: string }
  | { kind: "code"; value: string }
  | {
      kind: "table";
      header: string[];
      rows: string[][];
      aligns: Align[];
    };

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

function parseAligns(sep: string): Align[] {
  return splitRow(sep).map((c) => {
    const left = c.startsWith(":");
    const right = c.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    return "left";
  });
}

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEP = /^\s*\|[-:|\s]+\|\s*$/;

function parseBlocks(source: string): Block[] {
  const lines = source.split("\n");
  const blocks: Block[] = [];
  let textBuf: string[] = [];

  const flushText = () => {
    if (textBuf.length > 0) {
      blocks.push({ kind: "text", value: textBuf.join("\n") });
      textBuf = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Programming exercises author their code as ```python fences (same syntax
    // as Teori-MDX), but this renderer printed the fence markers and the code
    // as one raw prose run (found 2026-08-15 in the Ma2 kap 5 print sweep).
    // The language tag is dropped: print has no highlighter, monospace is the
    // whole point.
    if (line.trim().startsWith("```")) {
      const kodRader: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        kodRader.push(lines[i]);
        i++;
      }
      i++; // skip the closing fence (or run past the end on an unclosed one)
      flushText();
      blocks.push({ kind: "code", value: kodRader.join("\n") });
      continue;
    }
    if (
      TABLE_ROW.test(line) &&
      i + 1 < lines.length &&
      TABLE_SEP.test(lines[i + 1])
    ) {
      flushText();
      const header = splitRow(line);
      const aligns = parseAligns(lines[i + 1]);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ kind: "table", header, rows, aligns });
      continue;
    }
    textBuf.push(line);
    i++;
  }
  flushText();
  return blocks;
}

// Single-asterisk emphasis ("*är*") printed its markers verbatim (three places
// in Begreppet definition, one in Matematiska modeller, found 2026-08). The
// letter requirement keeps multiplication chains ("2*3*4") out: an emphasis
// run must contain a letter.
// A caret inside the run means the asterisks are multiplication in input
// syntax ("3*a^2*sqrt(3)/2" italicised a^2, found 2026-08-15) — not emphasis.
const EMPHASIS_RE = /(\*[^*\n^]*[A-Za-zÅÄÖåäö][^*\n^]*\*)/;

function splitEmphasis(
  text: string,
  keyPrefix: string,
  ordstodCtx: OrdstodRenderCtx | null,
) {
  const parts = text.split(EMPHASIS_RE);
  return parts.map((p, j) => {
    if (p.startsWith("*") && p.endsWith("*") && p.length >= 3) {
      return (
        <em key={`${keyPrefix}-e${j}`}>
          {maybeMarkOrdstod(p.slice(1, -1), `${keyPrefix}-e${j}`, ordstodCtx)}
        </em>
      );
    }
    return (
      <span key={`${keyPrefix}-e${j}`}>
        {maybeMarkOrdstod(p, `${keyPrefix}-e${j}`, ordstodCtx)}
      </span>
    );
  });
}

function splitBold(text: string, keyPrefix: string, ordstodCtx: OrdstodRenderCtx | null) {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((p, j) => {
    if (p.startsWith("**") && p.endsWith("**") && p.length >= 4) {
      const inner = p.slice(2, -2);
      return (
        <strong key={`${keyPrefix}-${j}`}>
          {maybeMarkOrdstod(inner, `${keyPrefix}-${j}`, ordstodCtx)}
        </strong>
      );
    }
    return (
      <span key={`${keyPrefix}-${j}`}>
        {splitEmphasis(p, `${keyPrefix}-${j}`, ordstodCtx)}
      </span>
    );
  });
}

// Punctuation that belongs to the formula before it rather than to the word
// after it. Opening brackets are absent on purpose: they belong to the right.
const SKILJETECKEN = /^[.,;:!?)\]}%‰]+/;

// A data list authored as ONE inline formula ("$312, 285, …$" — 15 values) is
// an atomic KaTeX box: it cannot wrap, so it overflowed the body column and
// ran under the illustration, hiding the last values (found 2026-08-15 in
// Kvartiler och percentiler). Long comma-separated formulas are split at
// top-level commas into one atom per item so the line can break between them.
// Depth tracking keeps "{,}" decimal commas and \mathbf{…} arguments intact.
function splitLangKommaLista(value: string): string[] | null {
  if (value.length <= 40) return null;
  const parts: string[] = [];
  let depth = 0;
  let buf = "";
  let prev = "";
  for (const ch of value) {
    if (ch === "{" || ch === "(") depth++;
    else if (ch === "}" || ch === ")") depth--;
    // "\," is LaTeX's thin space ("$1\,024\,000$") — splitting there tore the
    // formula into invalid fragments that KaTeX printed raw (found 2026-08-15
    // in Programmering som verktyg). Only a bare comma separates list items.
    if (ch === "," && depth === 0 && prev !== "\\") {
      parts.push(buf);
      buf = "";
    } else {
      buf += ch;
    }
    prev = ch;
  }
  parts.push(buf);
  if (parts.length < 4 || parts.some((p) => p.trim().length === 0)) return null;
  return parts.map((p) => p.trim());
}

function renderInline(
  source: string,
  keyPrefix: string,
  ordstodCtx: OrdstodRenderCtx | null,
) {
  const segments = parseSegments(source);
  return segments.map((s, i) => {
    // A sentence ending right after an inline formula can break between the two
    // and leave a lone "." on the next line: the formula is an atomic
    // inline-block, so the gap is a legal break opportunity. The punctuation is
    // therefore moved into a nowrap wrapper together with the formula — the
    // same fix rehype-formel-skiljetecken applies to MDX. (A U+2060 WORD JOINER
    // was measured 2026-08-14 and does NOT work: Blink breaks after the atomic
    // inline regardless.)
    const efter = segments[i + 1];
    if (s.kind === "text") {
      const fore = segments[i - 1];
      const redanLimmat =
        fore && fore.kind === "math" && !fore.block
          ? (s.value.match(SKILJETECKEN)?.[0].length ?? 0)
          : 0;
      const prosa = redanLimmat > 0 ? s.value.slice(redanLimmat) : s.value;
      if (prosa.length === 0) return null;
      // Inline code spans print as code, untouched; display-only repairs of
      // authored markup (step arrows, \texttt, \n, \%, …) and emphasis apply to
      // the prose around them. The maths inside $…$ never passes through here.
      return (
        <span key={`${keyPrefix}-${i}`}>
          {delaInlineKod(prosa).map((d, k) =>
            d.kod ? (
              <code
                key={k}
                className="rounded border border-border bg-bg px-1 font-mono text-[0.9em]"
              >
                {d.value}
              </code>
            ) : (
              <span key={k}>
                {splitBold(reparaProsa(d.value), `${keyPrefix}-${i}-${k}`, ordstodCtx)}
              </span>
            ),
          )}
        </span>
      );
    }
    if (!s.block) {
      const lista = splitLangKommaLista(s.value);
      if (lista) {
        // Each item keeps its comma inside a nowrap span (a bare break before
        // "," would strand it at a line start), with a plain space between
        // items as the break opportunity.
        return (
          <span key={`${keyPrefix}-${i}`}>
            {lista.map((item, k) => (
              <span key={k}>
                <span style={{ whiteSpace: "nowrap" }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: katex.renderToString(item, KATEX_OPTIONER),
                    }}
                  />
                  {k < lista.length - 1 ? "," : ""}
                </span>
                {k < lista.length - 1 ? " " : ""}
              </span>
            ))}
          </span>
        );
      }
    }
    const html = katex.renderToString(s.value, {
      ...KATEX_OPTIONER,
      displayMode: s.block,
    });
    if (s.block) {
      return (
        <span
          key={`${keyPrefix}-${i}`}
          className="block my-2"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    const limmat =
      efter && efter.kind === "text" ? (efter.value.match(SKILJETECKEN)?.[0] ?? "") : "";
    if (!limmat) {
      return (
        <span
          key={`${keyPrefix}-${i}`}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    return (
      <span key={`${keyPrefix}-${i}`} style={{ whiteSpace: "nowrap" }}>
        <span dangerouslySetInnerHTML={{ __html: html }} />
        {limmat}
      </span>
    );
  });
}

export default function InlineMath({
  source,
  ordstod = false,
}: InlineMathProps) {
  const blocks = useMemo(() => parseBlocks(source), [source]);
  const ordstodData = useOrdstod();

  // Built once per delavsnitt-payload change, not per render — former is a
  // plain Record because it crossed the server/klient-boundary via props;
  // matchText wants a Map. Only built when this call site opted in AND the
  // section actually has ordstöd data (server-side gating already resolved
  // that; null here just means "no marking").
  const ordstodCtx: OrdstodRenderCtx | null = useMemo(() => {
    if (!ordstod || !ordstodData) return null;
    return {
      data: ordstodData,
      formIndex: new Map(Object.entries(ordstodData.former)),
    };
  }, [ordstod, ordstodData]);

  return (
    <>
      {blocks.map((b, bi) => {
        if (b.kind === "text") {
          return (
            <span key={bi} style={{ whiteSpace: "pre-wrap" }}>
              {renderInline(b.value, `b${bi}`, ordstodCtx)}
            </span>
          );
        }
        if (b.kind === "code") {
          // pre-wrap, not overflow scroll: paper cannot scroll, and a long
          // data-list line clipped mid-token in print (5.2, 2026-08-15).
          return (
            <pre
              key={bi}
              className="my-2 rounded-md border border-foreground/15 bg-foreground/5 px-3 py-2 font-mono text-sm leading-6 whitespace-pre-wrap break-words"
            >
              <code>{b.value}</code>
            </pre>
          );
        }
        return (
          <table
            key={bi}
            className="my-3 border-collapse text-sm"
          >
            <thead>
              <tr>
                {b.header.map((c, ci) => (
                  <th
                    key={ci}
                    style={{ textAlign: b.aligns[ci] ?? "left" }}
                    className="border-b border-foreground/30 px-3 py-1.5 font-semibold"
                  >
                    {renderInline(c, `b${bi}-h${ci}`, ordstodCtx)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((c, ci) => (
                    <td
                      key={ci}
                      style={{ textAlign: b.aligns[ci] ?? "left" }}
                      className="border-b border-foreground/10 px-3 py-1"
                    >
                      {renderInline(c, `b${bi}-r${ri}-c${ci}`, ordstodCtx)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      })}
    </>
  );
}
